import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FolderKanban, Plus, Trash2, ChevronRight, Send, CheckCircle, DollarSign, Lock, TrendingUp, RefreshCw, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import CRMProjectDetail from './CRMProjectDetail';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = v => v != null && v > 0 ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00';

const CRMProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [form, setForm] = useState({ name: '', customer_company: '', description: '' });
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/projects?limit=100`, { credentials: 'include' });
      setProjects((await res.json()).projects);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/crm/projects`, { credentials: 'include',
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      const newProject = await res.json();
      toast.success('Project created');
      setShowForm(false);
      setForm({ name: '', customer_company: '', description: '' });
      await load();
      setSelectedProject(newProject.id);
    } catch { toast.error('Failed to create'); }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project and all files?')) return;
    await fetch(`${API}/api/crm/projects/${id}`, { credentials: 'include', method: 'DELETE' });
    toast.success('Deleted');
    await load();
  };

  const getStatus = (p) => {
    if (p.project_closed) return { key: 'closed', label: 'Closed', color: 'bg-slate-200 text-slate-700' };
    if (p.project_invoicing) return { key: 'invoicing', label: 'Invoicing', color: 'bg-indigo-100 text-indigo-800' };
    if (p.project_completed) return { key: 'completed', label: 'Completed', color: 'bg-sky-100 text-sky-800' };
    if (p.project_withdrawn) return { key: 'withdrawn', label: 'Withdrawn', color: 'bg-orange-100 text-orange-800' };
    if (p.project_accepted) return { key: 'accepted', label: 'Accepted', color: 'bg-emerald-100 text-emerald-800' };
    if (p.bid_submitted) return { key: 'submitted', label: 'Bid Submitted', color: 'bg-amber-100 text-amber-800' };
    return { key: 'planning', label: 'Planning', color: 'bg-blue-100 text-blue-800' };
  };

  // Compute filtered lists per status
  const byStatus = {
    submitted: projects.filter(p => p.bid_submitted && !p.project_accepted && !p.project_withdrawn && !p.project_closed),
    accepted:  projects.filter(p => p.project_accepted && !p.project_completed && !p.project_invoicing && !p.project_closed),
    withdrawn: projects.filter(p => p.project_withdrawn && !p.project_closed),
    completed: projects.filter(p => p.project_completed && !p.project_invoicing && !p.project_closed),
    invoicing: projects.filter(p => p.project_invoicing && !p.project_closed),
    closed:    projects.filter(p => p.project_closed),
  };

  // Back-compat aliases used elsewhere in this component
  const projectsAccepted = byStatus.accepted;
  const projectsSubmittedOnly = byStatus.submitted;
  const projectsInvoiced = byStatus.invoicing;
  const projectsClosed = byStatus.closed;

  const calcTotals = (list) => {
    const totalCV = list.reduce((s, p) => s + (p.original_contract_value || p.budget || 0), 0);
    const totalExpenses = list.reduce((s, p) => s + (p.cost_to_complete || 0), 0);
    const totalProfit = totalCV - totalExpenses;
    const margin = totalCV > 0 ? (totalProfit / totalCV) * 100 : 0;
    return { totalCV, totalExpenses, totalProfit, margin };
  };

  const acceptedTotals = calcTotals(projectsAccepted);
  const submittedTotals = calcTotals(projectsSubmittedOnly);
  const invoicedTotals = calcTotals(projectsInvoiced);
  const closedTotals = calcTotals(projectsClosed);
  const allTotals = calcTotals(projects);

  const getProjectMetrics = (p) => {
    const cv = p.original_contract_value || p.budget || 0;
    const expenses = p.cost_to_complete || 0;
    const profit = cv - expenses;
    const margin = cv > 0 ? (profit / cv) * 100 : 0;
    return { cv, profit, margin };
  };

  if (selectedProject) {
    return <CRMProjectDetail projectId={selectedProject} onBack={() => { setSelectedProject(null); load(); }} />;
  }

  return (
    <div className="space-y-4">
      {/* Summary Boxes */}
      {!loading && projects.length > 0 && (
        <div className="space-y-4">
          {/* PROJECT SNAPSHOT — Profits Box */}
          <Card className="border-blue-200 bg-blue-50/30" data-testid="project-snapshot-profits">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={16} className="text-blue-600" />
                <h3 className="text-sm font-semibold text-blue-800 uppercase tracking-wider">Project Snapshot</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-xs text-gray-500">Total Contract Value</p>
                  <p className="text-lg font-bold text-slate-900" data-testid="snapshot-total-cv">{fmt(allTotals.totalCV)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Total Expenses</p>
                  <p className="text-lg font-bold text-red-600" data-testid="snapshot-total-expenses">{fmt(allTotals.totalExpenses)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Total Profit</p>
                  <p className={`text-lg font-bold ${allTotals.totalProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="snapshot-total-profit">{fmt(allTotals.totalProfit)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Profit Margin</p>
                  <p className={`text-lg font-bold ${allTotals.margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="snapshot-margin">{allTotals.margin.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {/* Projects Accepted Box */}
          <Card className="border-emerald-200 bg-emerald-50/40" data-testid="projects-accepted-summary">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={16} className="text-emerald-600" />
                <h3 className="text-sm font-semibold text-emerald-800 uppercase tracking-wider">Projects Accepted</h3>
                <Badge className="bg-emerald-200 text-emerald-900 text-xs ml-auto">{projectsAccepted.length}</Badge>
              </div>
              {projectsAccepted.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {projectsAccepted.map(p => {
                    const m = getProjectMetrics(p);
                    return (
                      <div key={p.id} className="cursor-pointer hover:bg-emerald-100/60 px-2 py-1.5 rounded" onClick={() => setSelectedProject(p.id)}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-700 truncate mr-2 font-medium">{p.name}</span>
                          <span className="text-emerald-800 font-semibold whitespace-nowrap">{fmt(m.cv)}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          <span>Profit: <span className={m.profit >= 0 ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>{fmt(m.profit)}</span></span>
                          <span>Margin: <span className={m.margin >= 0 ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>{m.margin.toFixed(1)}%</span></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-emerald-600/60 text-xs py-2">No projects accepted yet</p>
              )}
              <div className="border-t border-emerald-200 pt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1"><DollarSign size={12} /> Total Contract Value</span>
                  <span className="text-lg font-bold text-emerald-900" data-testid="projects-accepted-total">{fmt(acceptedTotals.totalCV)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-emerald-600">Total Profit</span>
                  <span className={`text-sm font-bold ${acceptedTotals.totalProfit >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>{fmt(acceptedTotals.totalProfit)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-emerald-600">Profit Margin</span>
                  <span className={`text-sm font-bold ${acceptedTotals.margin >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>{acceptedTotals.margin.toFixed(1)}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Projects Submitted Box — RIGHT (excludes accepted) */}
          <Card className="border-amber-200 bg-amber-50/40" data-testid="projects-submitted-summary">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 mb-3">
                <Send size={16} className="text-amber-600" />
                <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wider">Projects Submitted</h3>
                <Badge className="bg-amber-200 text-amber-900 text-xs ml-auto">{projectsSubmittedOnly.length}</Badge>
              </div>
              {projectsSubmittedOnly.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {projectsSubmittedOnly.map(p => {
                    const m = getProjectMetrics(p);
                    return (
                      <div key={p.id} className="cursor-pointer hover:bg-amber-100/60 px-2 py-1.5 rounded" onClick={() => setSelectedProject(p.id)}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-700 truncate mr-2 font-medium">{p.name}</span>
                          <span className="text-amber-800 font-semibold whitespace-nowrap">{fmt(m.cv)}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          <span>Profit: <span className={m.profit >= 0 ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>{fmt(m.profit)}</span></span>
                          <span>Margin: <span className={m.margin >= 0 ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>{m.margin.toFixed(1)}%</span></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-amber-600/60 text-xs py-2">No projects submitted yet</p>
              )}
              <div className="border-t border-amber-200 pt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-amber-700 flex items-center gap-1"><DollarSign size={12} /> Total Contract Value</span>
                  <span className="text-lg font-bold text-amber-900" data-testid="projects-submitted-total">{fmt(submittedTotals.totalCV)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-amber-600">Total Profit</span>
                  <span className={`text-sm font-bold ${submittedTotals.totalProfit >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>{fmt(submittedTotals.totalProfit)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-amber-600">Profit Margin</span>
                  <span className={`text-sm font-bold ${submittedTotals.margin >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>{submittedTotals.margin.toFixed(1)}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoiced Box — between Submitted and Closed */}
          <Card className="border-indigo-200 bg-indigo-50/40" data-testid="projects-invoiced-summary">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 mb-3">
                <Receipt size={16} className="text-indigo-600" />
                <h3 className="text-sm font-semibold text-indigo-800 uppercase tracking-wider">Invoiced</h3>
                <Badge className="bg-indigo-200 text-indigo-900 text-xs ml-auto">{projectsInvoiced.length}</Badge>
              </div>
              {projectsInvoiced.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {projectsInvoiced.map(p => {
                    const m = getProjectMetrics(p);
                    return (
                      <div key={p.id} className="cursor-pointer hover:bg-indigo-100/60 px-2 py-1.5 rounded" onClick={() => setSelectedProject(p.id)}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-700 truncate mr-2 font-medium">{p.name}</span>
                          <span className="text-indigo-800 font-semibold whitespace-nowrap">{fmt(m.cv)}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          <span>Profit: <span className={m.profit >= 0 ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>{fmt(m.profit)}</span></span>
                          <span>Margin: <span className={m.margin >= 0 ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>{m.margin.toFixed(1)}%</span></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-indigo-600/60 text-xs py-2">No projects currently being invoiced</p>
              )}
              <div className="border-t border-indigo-200 pt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-indigo-700 flex items-center gap-1"><DollarSign size={12} /> Total Contract Value</span>
                  <span className="text-lg font-bold text-indigo-900" data-testid="projects-invoiced-total">{fmt(invoicedTotals.totalCV)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-indigo-600">Total Profit</span>
                  <span className={`text-sm font-bold ${invoicedTotals.totalProfit >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>{fmt(invoicedTotals.totalProfit)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-indigo-600">Profit Margin</span>
                  <span className={`text-sm font-bold ${invoicedTotals.margin >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>{invoicedTotals.margin.toFixed(1)}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Projects Closed-Out Box */}
          <Card className="border-slate-300 bg-slate-50/40" data-testid="projects-closed-summary">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 mb-3">
                <Lock size={16} className="text-slate-600" />
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Projects Closed-Out</h3>
                <Badge className="bg-slate-200 text-slate-800 text-xs ml-auto">{projectsClosed.length}</Badge>
              </div>
              {projectsClosed.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {projectsClosed.map(p => {
                    const m = getProjectMetrics(p);
                    return (
                      <div key={p.id} className="cursor-pointer hover:bg-slate-100/60 px-2 py-1.5 rounded" onClick={() => setSelectedProject(p.id)}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-700 truncate mr-2 font-medium">{p.name}</span>
                          <span className="text-slate-800 font-semibold whitespace-nowrap">{fmt(m.cv)}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          <span>Profit: <span className={m.profit >= 0 ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>{fmt(m.profit)}</span></span>
                          <span>Margin: <span className={m.margin >= 0 ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>{m.margin.toFixed(1)}%</span></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-500/60 text-xs py-2">No projects closed out yet</p>
              )}
              <div className="border-t border-slate-200 pt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600 flex items-center gap-1"><DollarSign size={12} /> Total Contract Value</span>
                  <span className="text-lg font-bold text-slate-800" data-testid="projects-closed-total">{fmt(closedTotals.totalCV)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Total Profit</span>
                  <span className={`text-sm font-bold ${closedTotals.totalProfit >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>{fmt(closedTotals.totalProfit)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Profit Margin</span>
                  <span className={`text-sm font-bold ${closedTotals.margin >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>{closedTotals.margin.toFixed(1)}%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{projects.length} projects</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="projects-refresh-btn">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button data-testid="add-project-btn" size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowForm(true)}>
            <Plus size={16} className="mr-1" /> New Project
          </Button>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="project-status-filter">
        {[
          { key: 'all', label: 'All', count: projects.length, color: 'bg-slate-700 text-white' },
          { key: 'submitted', label: 'Submitted', count: byStatus.submitted.length, color: 'bg-amber-500 text-white' },
          { key: 'accepted',  label: 'Accepted',  count: byStatus.accepted.length,  color: 'bg-emerald-600 text-white' },
          { key: 'withdrawn', label: 'Withdrawn', count: byStatus.withdrawn.length, color: 'bg-orange-500 text-white' },
          { key: 'completed', label: 'Completed', count: byStatus.completed.length, color: 'bg-sky-600 text-white' },
          { key: 'invoicing', label: 'Invoicing', count: byStatus.invoicing.length, color: 'bg-indigo-600 text-white' },
          { key: 'closed',    label: 'Closed',    count: byStatus.closed.length,    color: 'bg-slate-500 text-white' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setStatusFilter(tab.key)}
            data-testid={`project-status-tab-${tab.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              statusFilter === tab.key
                ? tab.color + ' shadow-sm scale-105'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}>
            {tab.label}
            <span className={`ml-1.5 inline-block min-w-[18px] text-center text-[10px] font-semibold rounded-full px-1 ${
              statusFilter === tab.key ? 'bg-white/30 text-white' : 'bg-slate-200 text-slate-700'
            }`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {showForm && (
        <Card className="border-indigo-200">
          <CardContent className="pt-5">
            <form onSubmit={handleCreate} className="space-y-3">
              <input data-testid="new-project-name" required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                placeholder="Project Name *" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              <input value={form.customer_company} onChange={e => setForm(f => ({...f, customer_company: e.target.value}))}
                placeholder="Customer Company" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                placeholder="Description" rows={2} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              <div className="flex gap-2">
                <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700">Create Project</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" /></div>
      : projects.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400"><FolderKanban className="mx-auto mb-2" size={32} /><p>No projects yet</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {(statusFilter === 'all' ? projects : (byStatus[statusFilter] || [])).map(p => {
            const status = getStatus(p);
            const val = p.original_contract_value || p.budget || 0;
            const inv = p.invoice_summary || { total_invoiced: 0, total_paid: 0, count: 0, status: 'none' };
            const invStatusCls = {
              paid: 'bg-emerald-100 text-emerald-800',
              partial: 'bg-amber-100 text-amber-800',
              pending: 'bg-blue-100 text-blue-800',
              none: 'bg-slate-100 text-slate-500',
            }[inv.status] || 'bg-slate-100 text-slate-500';
            const invLabel = { paid: 'Paid', partial: 'Partial', pending: 'Pending', none: 'No Invoices' }[inv.status] || 'No Invoices';
            return (
              <Card key={p.id} className="hover:shadow-sm cursor-pointer transition-shadow" onClick={() => setSelectedProject(p.id)} data-testid={`project-${p.id}`}>
                <CardContent className="py-4 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-slate-900">{p.name}</h4>
                      {p.project_number && <Badge className="bg-slate-800 text-white text-xs font-mono">{p.project_number}</Badge>}
                      <Badge className={status.color}>{status.label}</Badge>
                      {val > 0 && <span className="text-sm font-semibold text-slate-600">{fmt(val)}</span>}
                    </div>
                    <div className="flex gap-3 mt-1 text-sm text-gray-500 flex-wrap items-center">
                      {p.customer_company && <span>{p.customer_company}</span>}
                      <span>{new Date(p.created_at).toLocaleDateString()}</span>
                      <span className="inline-flex items-center gap-1.5" data-testid={`project-invoiced-${p.id}`}>
                        <Receipt size={12} className="text-gray-400" />
                        <span className="font-medium text-slate-700">Invoiced: {fmt(inv.total_invoiced)}</span>
                        <Badge className={`${invStatusCls} text-[10px]`}>{invLabel}</Badge>
                      </span>
                    </div>
                    {(p.sub_quotes || []).some(sq => sq.po_number) && (
                      <div className="flex gap-1.5 mt-1.5 flex-wrap">
                        {p.sub_quotes.filter(sq => sq.po_number).map(sq => (
                          <button key={sq.id} onClick={(e) => { e.stopPropagation(); if (sq.serve_path) window.open(`${API}/api${encodeURI(sq.serve_path)}`, '_blank'); else setSelectedProject(p.id); }}
                            title={sq.filename ? `View: ${sq.filename}` : sq.po_number}>
                            <Badge className="bg-indigo-50 text-indigo-700 text-xs font-mono hover:bg-indigo-100 cursor-pointer">{sq.po_number}</Badge>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" className="text-red-400" onClick={(e) => handleDelete(e, p.id)}><Trash2 size={14} /></Button>
                    <ChevronRight size={18} className="text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CRMProjects;
